import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import {
    X, Save, Trash2, Upload as UploadIcon, Plus, RefreshCw, ExternalLink,
    Image as ImageIcon, Film, Music, Camera, Eye, EyeOff, Settings2, Home,
    FileVideo, Edit3, Lock, FolderUp, CheckCircle2, AlertCircle,
} from "lucide-react";
import { useAdmin } from "../contexts/AdminContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTION_ORDER = [
    "caprio-vibe-tv", "merch-store",
    "center-hub", "p1", "p2", "p3", "p4", "p5", "p6", "p7", "p8",
    "garage", "candle-lab", "mixologist-lab", "hg-sea-vessel", "login",
];

const PANO_SLOTS_BY_KIND = {
    center: ["front", "rear"],
    basic: ["front"],
    hallway: ["hallway", "interior"],
};

/**
 * Big modal admin panel: edit every section + global config in one place.
 */
export default function AdminPanel({ open, onClose, sections, appConfig, onSectionUpdated, onConfigUpdated, onResetAll, onUpscaleAll }) {
    const accent = "#D4AF37";
    const { lock } = useAdmin();
    const [page, setPage] = useState("splash"); // 'splash' | section id
    const [upscaling, setUpscaling] = useState(false);

    if (!open) return null;

    const visiblePages = [
        { id: "splash", label: "Splash & Config", icon: <Home size={14} /> },
        { id: "bulk-4k", label: "Bulk 4K Upload", icon: <UploadIcon size={14} /> },
        ...SECTION_ORDER
            .map((id) => sections.find((s) => s.id === id))
            .filter(Boolean)
            .map((s) => ({ id: s.id, label: s.custom_label || s.label, icon: <Edit3 size={14} /> })),
    ];

    return createPortal((
        <div
            data-testid="admin-panel"
            className="fixed inset-0 z-[150] flex items-center justify-center px-2 py-6"
            style={{ background: "rgba(0,0,0,0.92)", backdropFilter: "blur(14px)" }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
        >
            <div
                className="glass rounded-2xl w-full max-w-6xl h-[92vh] flex overflow-hidden"
                style={{ border: `1px solid ${accent}55`, boxShadow: `0 40px 100px -20px ${accent}66` }}
            >
                {/* Sidebar */}
                <aside
                    className="w-60 md:w-72 shrink-0 overflow-y-auto py-5 px-3 border-r"
                    style={{ borderColor: `${accent}22`, background: "rgba(0,0,0,0.45)" }}
                    data-testid="admin-sidebar"
                >
                    <div className="px-2 mb-5">
                        <div className="text-[10px] tracking-[0.4em] uppercase" style={{ color: accent }}>
                            Admin Panel
                        </div>
                        <div className="font-serif-display text-xl uppercase tracking-luxe mt-1" style={{ color: "#fff" }}>
                            Caprio Chateau
                        </div>
                    </div>
                    <div className="space-y-1">
                        {visiblePages.map((p) => (
                            <button
                                key={p.id}
                                type="button"
                                onClick={() => setPage(p.id)}
                                data-testid={`admin-nav-${p.id}`}
                                className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-[11px] tracking-[0.2em] uppercase transition-colors"
                                style={{
                                    background: page === p.id ? `${accent}25` : "transparent",
                                    color: page === p.id ? accent : "rgba(255,255,255,0.78)",
                                    border: `1px solid ${page === p.id ? accent + "60" : "transparent"}`,
                                }}
                            >
                                <span style={{ color: page === p.id ? accent : "rgba(255,255,255,0.6)" }}>{p.icon}</span>
                                <span className="truncate">{p.label}</span>
                            </button>
                        ))}
                    </div>

                    <div className="mt-6 px-2 space-y-2">
                        {onUpscaleAll && (
                            <button
                                type="button"
                                onClick={async () => {
                                    setUpscaling(true);
                                    try { await onUpscaleAll(); } finally { setUpscaling(false); }
                                }}
                                disabled={upscaling}
                                data-testid="admin-upscale-all"
                                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full text-[10px] tracking-[0.25em] uppercase disabled:opacity-60"
                                style={{ background: `${accent}18`, color: accent, border: `1px solid ${accent}55` }}
                            >
                                <ImageIcon size={12} /> {upscaling ? "Upscaling…" : "Upscale to 4K"}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onResetAll}
                            data-testid="admin-reset-all"
                            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full text-[10px] tracking-[0.25em] uppercase"
                            style={{ background: "rgba(255,255,255,0.04)", color: "#ff9a9a", border: "1px solid rgba(255,154,154,0.4)" }}
                        >
                            <RefreshCw size={12} /> Reset All
                        </button>
                        <button
                            type="button"
                            onClick={() => { lock(); onClose && onClose(); }}
                            data-testid="admin-logout"
                            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full text-[10px] tracking-[0.25em] uppercase"
                            style={{ background: "rgba(255,255,255,0.04)", color: accent, border: `1px solid ${accent}55` }}
                        >
                            <Lock size={12} /> Lock & Close
                        </button>
                    </div>
                </aside>

                {/* Main content */}
                <main className="flex-1 overflow-y-auto p-6 md:p-8 relative">
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="admin-close"
                        className="absolute top-4 right-4 w-10 h-10 rounded-full grid place-items-center"
                        style={{ background: `${accent}18`, color: accent, border: `1px solid ${accent}55` }}
                    >
                        <X size={18} />
                    </button>

                    {page === "splash" && (
                        <SplashEditor
                            config={appConfig}
                            onConfigUpdated={onConfigUpdated}
                            accent={accent}
                        />
                    )}

                    {page === "bulk-4k" && (
                        <BulkUploader
                            accent={accent}
                            onSectionsRefresh={async () => {
                                try {
                                    const res = await axios.get(`${API}/sections`);
                                    res.data.forEach((s) => onSectionUpdated && onSectionUpdated(s));
                                } catch (e) { console.error(e); }
                            }}
                        />
                    )}

                    {page !== "splash" && page !== "bulk-4k" && (
                        <SectionEditor
                            key={page}
                            section={sections.find((s) => s.id === page)}
                            onUpdated={onSectionUpdated}
                            accent={accent}
                        />
                    )}
                </main>
            </div>
        </div>
    ), document.body);
}


/* -------- Splash & global config editor -------- */
function SplashEditor({ config, onConfigUpdated, accent }) {
    const [title, setTitle] = useState(config?.splash_title || "Drive into Caprio Chateau");
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => { setTitle(config?.splash_title || "Drive into Caprio Chateau"); }, [config]);

    const handleSaveTitle = async () => {
        setSaving(true);
        try {
            const res = await axios.patch(`${API}/config`, {
                splash_title: title,
                splash_video_url: config?.splash_video_url || "",
                music_bar_p7_only: !!config?.music_bar_p7_only,
            });
            onConfigUpdated && onConfigUpdated(res.data);
        } catch (e) { console.error(e); } finally { setSaving(false); }
    };

    const handleVideo = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await axios.post(`${API}/config/splash-video`, fd);
            onConfigUpdated && onConfigUpdated(res.data);
        } catch (err) { console.error(err); } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const handleClearVideo = async () => {
        try {
            const res = await axios.patch(`${API}/config`, {
                splash_title: config?.splash_title || "Drive into Caprio Chateau",
                splash_video_url: "",
                music_bar_p7_only: !!config?.music_bar_p7_only,
            });
            onConfigUpdated && onConfigUpdated(res.data);
        } catch (e) { console.error(e); }
    };

    return (
        <div data-testid="admin-splash-editor" className="max-w-2xl">
            <SectionTitle accent={accent} eyebrow="Global · Splash" title="The Estate Gate" />

            <Field label="Drive Button Label">
                <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    data-testid="admin-splash-title-input"
                    className="w-full rounded-xl px-4 py-2 text-sm outline-none"
                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                />
                <button
                    type="button"
                    onClick={handleSaveTitle}
                    disabled={saving}
                    data-testid="admin-splash-title-save"
                    className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase disabled:opacity-50"
                    style={{ background: accent, color: "#0a0a0a" }}
                >
                    <Save size={12} /> {saving ? "Saving…" : "Save Title"}
                </button>
            </Field>

            <Field label="Splash Video">
                {config?.splash_video_url ? (
                    <div className="rounded-xl p-3 mb-3" style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${accent}40` }}>
                        <video
                            src={config.splash_video_url}
                            controls
                            data-testid="admin-splash-video-preview"
                            className="w-full rounded-lg max-h-72 bg-black"
                        />
                        <div className="mt-2 flex items-center justify-between gap-2">
                            <code className="text-[10px] truncate opacity-60" style={{ color: "#fff" }}>
                                …{(config.splash_video_url || "").split("/").pop()}
                            </code>
                            <button
                                type="button"
                                onClick={handleClearVideo}
                                data-testid="admin-splash-video-clear"
                                className="inline-flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full"
                                style={{ background: "transparent", color: "#ff9a9a", border: "1px solid rgba(255,154,154,0.45)" }}
                            >
                                <Trash2 size={11} /> Clear
                            </button>
                        </div>
                    </div>
                ) : (
                    <div
                        className="rounded-xl p-4 mb-3 text-center"
                        style={{ background: "rgba(255,255,255,0.03)", border: `1px dashed ${accent}55`, color: "rgba(255,255,255,0.55)" }}
                    >
                        <FileVideo size={28} className="mx-auto mb-2 opacity-60" />
                        <p className="text-[11px] tracking-[0.3em] uppercase">No splash video yet</p>
                    </div>
                )}
                <input
                    ref={fileRef}
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime,video/x-m4v,video/ogg"
                    className="hidden"
                    data-testid="admin-splash-video-input"
                    onChange={handleVideo}
                />
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="admin-splash-video-upload"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase disabled:opacity-50"
                    style={{ background: accent, color: "#0a0a0a" }}
                >
                    <UploadIcon size={12} /> {uploading ? "Uploading…" : (config?.splash_video_url ? "Replace Video" : "Upload Video")}
                </button>
            </Field>

            <Field label="Music Bar Behavior">
                <label
                    className="flex items-center gap-3 p-3 rounded-xl cursor-pointer select-none"
                    style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${accent}40` }}
                >
                    <input
                        type="checkbox"
                        data-testid="admin-music-bar-p7-only"
                        checked={!!config?.music_bar_p7_only}
                        onChange={async (e) => {
                            try {
                                const res = await axios.patch(`${API}/config`, {
                                    splash_title: config?.splash_title || "Drive into Caprio Chateau",
                                    splash_video_url: config?.splash_video_url || "",
                                    music_bar_p7_only: e.target.checked,
                                });
                                onConfigUpdated && onConfigUpdated(res.data);
                            } catch (err) { console.error(err); }
                        }}
                        className="h-4 w-4 accent-yellow-500"
                    />
                    <div>
                        <div className="text-sm" style={{ color: "#fff" }}>
                            Show music bar on Recording Studio only
                        </div>
                        <div className="text-[10px] tracking-[0.15em] uppercase opacity-70 mt-1" style={{ color: "#fff" }}>
                            When on, the audio player never appears on other tabs — even for admins.
                        </div>
                    </div>
                </label>
            </Field>
        </div>
    );
}


/* -------- Section editor -------- */
function SectionEditor({ section, onUpdated, accent }) {
    if (!section) return null;

    return (
        <div data-testid={`admin-section-${section.id}`} className="max-w-3xl space-y-8">
            <SectionTitle
                accent={accent}
                eyebrow={`Section · ${section.id}`}
                title={section.custom_label || section.label}
            />

            <BasicFields section={section} onUpdated={onUpdated} accent={accent} />
            {section.kind === "iframe" && <IframeEditor section={section} onUpdated={onUpdated} accent={accent} />}
            {section.kind === "merch" && <MerchEditor section={section} onUpdated={onUpdated} accent={accent} />}
            {section.kind !== "iframe" && section.kind !== "merch" && (
                <>
                    <PanoramaUploader section={section} onUpdated={onUpdated} accent={accent} />
                    {section.feature === "bio" && <BioEditor section={section} onUpdated={onUpdated} accent={accent} />}
                    {(section.feature === "events" || section.id === "garage") && <EventsEditor section={section} onUpdated={onUpdated} accent={accent} />}
                    {(section.kind === "bio" || section.feature === "bio" || section.id === "login") && (
                        <LinksEditor section={section} onUpdated={onUpdated} accent={accent} />
                    )}
                    <MediaUploader section={section} kind="video" onUpdated={onUpdated} accent={accent} />
                    <MediaUploader section={section} kind="audio" onUpdated={onUpdated} accent={accent} />
                    <MediaUploader section={section} kind="photo" onUpdated={onUpdated} accent={accent} />
                </>
            )}
        </div>
    );
}


function IframeEditor({ section, onUpdated, accent }) {
    const [url, setUrl] = useState(section.iframe_url || "");
    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true);
        try {
            const res = await axios.patch(`${API}/sections/${section.id}`, { iframe_url: url.trim() });
            onUpdated && onUpdated(res.data);
        } catch (e) { console.error(e); } finally { setSaving(false); }
    };
    return (
        <Group title="Embed URL" accent={accent}>
            <p className="text-[11px] opacity-75 mb-3" style={{ color: "#fff" }}>
                The URL loaded inside this tab. Works with Caprio Vibe TV, YouTube, Vimeo, live streams, and most sites that allow embedding.
            </p>
            <Field label="Iframe URL">
                <input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://caprio-vibe-tv.preview.emergentagent.com/"
                    data-testid={`admin-iframe-url-${section.id}`}
                    className="w-full rounded-xl px-4 py-2 text-sm outline-none"
                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                />
            </Field>
            <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid={`admin-iframe-save-${section.id}`}
                className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase disabled:opacity-50"
                style={{ background: accent, color: "#0a0a0a" }}
            >
                <Save size={12} /> {saving ? "Saving…" : "Save URL"}
            </button>
        </Group>
    );
}


function MerchEditor({ section, onUpdated, accent }) {
    const [domain, setDomain] = useState(section.shopify_domain || "");
    const [products, setProducts] = useState(Array.isArray(section.products) ? section.products : []);
    const [draft, setDraft] = useState({ title: "", price: "", image_url: "", url: "" });
    const [saving, setSaving] = useState(false);

    const saveAll = async (next) => {
        setSaving(true);
        try {
            const res = await axios.patch(`${API}/sections/${section.id}`, {
                shopify_domain: domain.trim(),
                products: next ?? products,
                has_content: (next ?? products).length > 0,
            });
            onUpdated && onUpdated(res.data);
            if (next) setProducts(res.data.products || next);
        } catch (e) { console.error(e); } finally { setSaving(false); }
    };

    const addProduct = () => {
        if (!draft.title.trim()) return;
        const next = [...products, { ...draft, title: draft.title.trim() }];
        setProducts(next);
        setDraft({ title: "", price: "", image_url: "", url: "" });
        saveAll(next);
    };

    const removeProduct = (idx) => {
        const next = products.filter((_, i) => i !== idx);
        setProducts(next);
        saveAll(next);
    };

    return (
        <Group title="Shopify Store" accent={accent}>
            <p className="text-[11px] opacity-75 mb-3" style={{ color: "#fff" }}>
                While the store is empty this page shows a “Coming Soon” panel. Add products below (title, price, image URL, link to the Shopify product) and they appear as a live merch grid.
            </p>
            <Field label="Shopify Domain (optional)">
                <input
                    type="text"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    onBlur={() => saveAll()}
                    placeholder="caprio-chateau.myshopify.com"
                    data-testid={`admin-shopify-domain-${section.id}`}
                    className="w-full rounded-xl px-4 py-2 text-sm outline-none"
                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                />
            </Field>

            {products.length > 0 && (
                <div className="space-y-2 mb-4" data-testid="admin-merch-list">
                    {products.map((p, i) => (
                        <div
                            key={p.id || i}
                            className="flex items-center gap-3 p-2 rounded-xl"
                            style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${accent}33` }}
                        >
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-black shrink-0">
                                {p.image_url ? (
                                    <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full grid place-items-center opacity-50" style={{ color: accent }}>
                                        <ImageIcon size={16} />
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm truncate" style={{ color: "#fff" }}>{p.title || "Untitled"}</div>
                                <div className="text-[10px] tracking-[0.2em] uppercase opacity-70" style={{ color: "#fff" }}>
                                    {p.price || "—"}
                                </div>
                            </div>
                            {p.url && (
                                <a
                                    href={p.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[10px] tracking-[0.2em] uppercase"
                                    style={{ color: accent }}
                                    data-testid={`admin-merch-visit-${i}`}
                                >
                                    Open
                                </a>
                            )}
                            <button
                                type="button"
                                onClick={() => removeProduct(i)}
                                data-testid={`admin-merch-remove-${i}`}
                                className="w-7 h-7 rounded-full grid place-items-center"
                                style={{ background: "rgba(255,154,154,0.1)", color: "#ff9a9a" }}
                            >
                                <Trash2 size={12} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className="rounded-2xl p-3 space-y-2" style={{ background: "rgba(255,255,255,0.03)", border: `1px dashed ${accent}55` }}>
                <div className="text-[10px] tracking-[0.3em] uppercase mb-1" style={{ color: accent }}>Add product</div>
                <Field label="Title">
                    <input
                        type="text"
                        value={draft.title}
                        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                        placeholder="Caprio Chateau · Signature Candle"
                        data-testid={`admin-merch-title-${section.id}`}
                        className="w-full rounded-xl px-4 py-2 text-sm outline-none"
                        style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}40` }}
                    />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                    <Field label="Price">
                        <input
                            type="text"
                            value={draft.price}
                            onChange={(e) => setDraft({ ...draft, price: e.target.value })}
                            placeholder="$48.00"
                            data-testid={`admin-merch-price-${section.id}`}
                            className="w-full rounded-xl px-4 py-2 text-sm outline-none"
                            style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}40` }}
                        />
                    </Field>
                    <Field label="Image URL">
                        <input
                            type="url"
                            value={draft.image_url}
                            onChange={(e) => setDraft({ ...draft, image_url: e.target.value })}
                            placeholder="https://…jpg"
                            data-testid={`admin-merch-image-${section.id}`}
                            className="w-full rounded-xl px-4 py-2 text-sm outline-none"
                            style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}40` }}
                        />
                    </Field>
                </div>
                <Field label="Shopify product URL">
                    <input
                        type="url"
                        value={draft.url}
                        onChange={(e) => setDraft({ ...draft, url: e.target.value })}
                        placeholder="https://caprio-chateau.myshopify.com/products/signature-candle"
                        data-testid={`admin-merch-url-${section.id}`}
                        className="w-full rounded-xl px-4 py-2 text-sm outline-none"
                        style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}40` }}
                    />
                </Field>
                <button
                    type="button"
                    onClick={addProduct}
                    disabled={saving || !draft.title.trim()}
                    data-testid={`admin-merch-add-${section.id}`}
                    className="mt-1 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase disabled:opacity-50"
                    style={{ background: accent, color: "#0a0a0a" }}
                >
                    <Plus size={12} /> Add product
                </button>
            </div>
        </Group>
    );
}


function BasicFields({ section, onUpdated, accent }) {
    const [label, setLabel] = useState(section.custom_label || "");
    const [desc, setDesc] = useState(section.description || "");
    const [hidden, setHidden] = useState(!!section.hidden_in_menu);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await axios.patch(`${API}/sections/${section.id}`, {
                custom_label: label.trim() || null,
                description: desc,
                hidden_in_menu: hidden,
            });
            onUpdated && onUpdated(res.data);
        } catch (e) { console.error(e); } finally { setSaving(false); }
    };

    return (
        <Group title="Basics" accent={accent}>
            <Field label="Title (shown in tab & lower‑left heading)">
                <input
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder={section.label}
                    data-testid={`admin-label-input-${section.id}`}
                    className="w-full rounded-xl px-4 py-2 text-sm outline-none"
                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                />
            </Field>
            <Field label="Description (short caption under the title)">
                <textarea
                    rows={3}
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    data-testid={`admin-desc-input-${section.id}`}
                    className="w-full rounded-xl px-4 py-2 text-sm outline-none"
                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                />
            </Field>
            <label className="flex items-center gap-3 cursor-pointer">
                <input
                    type="checkbox"
                    checked={hidden}
                    onChange={(e) => setHidden(e.target.checked)}
                    data-testid={`admin-hidden-toggle-${section.id}`}
                    className="w-4 h-4 accent-amber-500"
                />
                <span className="text-[11px] tracking-[0.25em] uppercase" style={{ color: "rgba(255,255,255,0.85)" }}>
                    {hidden ? <EyeOff size={11} className="inline mr-1" /> : <Eye size={11} className="inline mr-1" />}
                    Hide from menu
                </span>
            </label>
            <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                data-testid={`admin-basics-save-${section.id}`}
                className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase disabled:opacity-50"
                style={{ background: accent, color: "#0a0a0a" }}
            >
                <Save size={12} /> {saving ? "Saving…" : "Save Basics"}
            </button>

            <ShareUrlField section={section} accent={accent} />
        </Group>
    );
}


function ShareUrlField({ section, accent }) {
    const [copied, setCopied] = React.useState(false);
    const origin = (typeof window !== "undefined" && window.location && window.location.origin) || "";
    const path = section.id === "center-hub" ? "/" : `/${section.id}`;
    const shareUrl = `${origin}${path}`;
    return (
        <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${accent}30` }}>
            <div className="text-[10px] tracking-[0.35em] uppercase mb-2" style={{ color: accent }}>
                Shareable Link
            </div>
            <div className="flex items-center gap-2">
                <input
                    type="text"
                    readOnly
                    value={shareUrl}
                    onFocus={(e) => e.target.select()}
                    data-testid={`admin-share-url-${section.id}`}
                    className="flex-1 rounded-xl px-4 py-2 text-xs outline-none"
                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}40` }}
                />
                <button
                    type="button"
                    onClick={async () => {
                        try {
                            await navigator.clipboard.writeText(shareUrl);
                            setCopied(true);
                            setTimeout(() => setCopied(false), 1500);
                        } catch (_e) {
                            // Fallback: select() so the user can Cmd/Ctrl+C
                            const el = document.querySelector(`[data-testid="admin-share-url-${section.id}"]`);
                            if (el && el.select) el.select();
                        }
                    }}
                    data-testid={`admin-share-copy-${section.id}`}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-[10px] tracking-[0.25em] uppercase shrink-0"
                    style={{ background: accent, color: "#0a0a0a" }}
                >
                    {copied ? "Copied" : "Copy"}
                </button>
            </div>
            <p className="mt-1 text-[10px] opacity-70" style={{ color: "#fff" }}>
                Send this URL in your promos — it opens the site directly on this tab.
            </p>
        </div>
    );
}


function PanoramaUploader({ section, onUpdated, accent }) {
    const views = PANO_SLOTS_BY_KIND[section.kind] || PANO_SLOTS_BY_KIND.basic;
    const slots = [];
    views.forEach((v) => {
        slots.push({ kind: "panorama", view: v, mode: "day", label: `${v} · Day` });
        slots.push({ kind: "panorama", view: v, mode: "night", label: `${v} · Night` });
    });
    slots.push({ kind: "iso", view: "iso", mode: "day", label: "ISO · Day" });
    slots.push({ kind: "iso", view: "iso", mode: "night", label: "ISO · Night" });

    return (
        <Group title="Panoramas & ISO" accent={accent}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {slots.map((s) => (
                    <PanoramaSlot key={`${s.view}-${s.mode}-${s.kind}`} slot={s} section={section} onUpdated={onUpdated} accent={accent} />
                ))}
            </div>
        </Group>
    );
}

function PanoramaSlot({ slot, section, onUpdated, accent }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const url = slot.kind === "iso"
        ? section?.iso?.[slot.mode]
        : section?.panoramas?.[slot.view]?.[slot.mode];

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("view", slot.view);
            fd.append("mode", slot.mode);
            fd.append("kind", slot.kind);
            const res = await axios.post(`${API}/sections/${section.id}/upload`, fd);
            onUpdated && onUpdated(res.data);
        } catch (err) { console.error(err); } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <div
            data-testid={`admin-pano-slot-${section.id}-${slot.kind}-${slot.view}-${slot.mode}`}
            onClick={() => inputRef.current?.click()}
            className="relative rounded-xl p-3 cursor-pointer overflow-hidden transition-transform hover:scale-[1.02]"
            style={{
                background: "rgba(255,255,255,0.04)",
                border: `1px ${url ? "solid" : "dashed"} ${accent}55`,
                minHeight: 92,
            }}
        >
            {url && (
                <div
                    className="absolute inset-0 opacity-35"
                    style={{ backgroundImage: `url(${url})`, backgroundSize: "cover", backgroundPosition: "center" }}
                />
            )}
            <div className="relative flex items-center justify-between">
                <div>
                    <div className="text-[10px] tracking-[0.22em] uppercase" style={{ color: accent }}>{slot.label}</div>
                    <div className="text-[11px] mt-1" style={{ color: "rgba(255,255,255,0.85)" }}>
                        {uploading ? "Uploading…" : (url ? "Replace image" : "Click to upload")}
                    </div>
                </div>
                <div
                    className="w-8 h-8 rounded-full grid place-items-center"
                    style={{ background: url ? `${accent}30` : "transparent", border: `1px solid ${accent}55`, color: accent }}
                >
                    <ImageIcon size={13} />
                </div>
            </div>
            <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                className="hidden"
                data-testid={`admin-pano-input-${section.id}-${slot.kind}-${slot.view}-${slot.mode}`}
                onChange={handleFile}
            />
        </div>
    );
}


function BioEditor({ section, onUpdated, accent }) {
    const bio = section.bio || { text: "", image_url: "", role_title: "Captain of this Vessel" };
    const [text, setText] = useState(bio.text || "");
    const [role, setRole] = useState(bio.role_title || "Captain of this Vessel");
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await axios.patch(`${API}/sections/${section.id}`, {
                bio: { text, role_title: role, image_url: section.bio?.image_url || "" },
            });
            onUpdated && onUpdated(res.data);
        } catch (e) { console.error(e); } finally { setSaving(false); }
    };

    const handleImage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await axios.post(`${API}/sections/${section.id}/bio-image`, fd);
            onUpdated && onUpdated(res.data);
        } catch (err) { console.error(err); } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <Group title="Captain's Bio" accent={accent}>
            <Field label="Role Title">
                <input
                    type="text"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    data-testid={`admin-bio-role-${section.id}`}
                    className="w-full rounded-xl px-4 py-2 text-sm outline-none"
                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                />
            </Field>
            <Field label="Bio Text">
                <textarea
                    rows={5}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    data-testid={`admin-bio-text-${section.id}`}
                    className="w-full rounded-xl px-4 py-2 text-sm outline-none"
                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                />
            </Field>
            <Field label="Portrait">
                {section.bio?.image_url && (
                    <img src={section.bio.image_url} alt="bio" className="w-32 h-40 object-cover rounded-xl mb-2" style={{ border: `1px solid ${accent}55` }} />
                )}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImage} data-testid={`admin-bio-image-input-${section.id}`} />
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid={`admin-bio-image-upload-${section.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase disabled:opacity-50"
                    style={{ background: `${accent}15`, color: accent, border: `1px solid ${accent}55` }}
                >
                    <Camera size={12} /> {uploading ? "Uploading…" : (section.bio?.image_url ? "Replace Portrait" : "Upload Portrait")}
                </button>
            </Field>
            <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                data-testid={`admin-bio-save-${section.id}`}
                className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase disabled:opacity-50"
                style={{ background: accent, color: "#0a0a0a" }}
            >
                <Save size={12} /> {saving ? "Saving…" : "Save Bio"}
            </button>
        </Group>
    );
}


function LinksEditor({ section, onUpdated, accent }) {
    const isLogin = section.id === "login";
    const maxLinks = isLogin ? 9 : 3;
    const initial = section.links?.length
        ? section.links.slice(0, maxLinks)
        : Array.from({ length: maxLinks }).map((_, i) => ({ title: isLogin ? `App ${i + 1}` : `Link ${i + 1}`, url: "" }));
    const [links, setLinks] = useState(initial);
    const [saving, setSaving] = useState(false);

    const updateLink = (i, key, val) => {
        const next = links.slice();
        next[i] = { ...next[i], [key]: val };
        setLinks(next);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await axios.patch(`${API}/sections/${section.id}`, { links });
            onUpdated && onUpdated(res.data);
        } catch (e) { console.error(e); } finally { setSaving(false); }
    };

    return (
        <Group title={isLogin ? "Blockchain Nodes (9 links)" : "Candle Shop Links (3)"} accent={accent}>
            <div className="space-y-2">
                {links.map((link, i) => (
                    <div key={i} className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                        <input
                            type="text"
                            value={link.title}
                            onChange={(e) => updateLink(i, "title", e.target.value)}
                            placeholder={isLogin ? `App ${i + 1}` : "Title"}
                            data-testid={`admin-link-title-${section.id}-${i}`}
                            className="sm:col-span-2 rounded-xl px-3 py-2 text-xs outline-none"
                            style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                        />
                        <input
                            type="url"
                            value={link.url}
                            onChange={(e) => updateLink(i, "url", e.target.value)}
                            placeholder="https://"
                            data-testid={`admin-link-url-${section.id}-${i}`}
                            className="sm:col-span-3 rounded-xl px-3 py-2 text-xs outline-none"
                            style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                        />
                    </div>
                ))}
            </div>
            <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                data-testid={`admin-links-save-${section.id}`}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase disabled:opacity-50"
                style={{ background: accent, color: "#0a0a0a" }}
            >
                <Save size={12} /> {saving ? "Saving…" : "Save Links"}
            </button>
        </Group>
    );
}


function EventsEditor({ section, onUpdated, accent }) {
    const events = section.events || [];
    const [draft, setDraft] = useState({ title: "", datetime: "", description: "", url: "" });
    const [saving, setSaving] = useState(false);

    const handleAdd = async () => {
        if (!draft.title || !draft.datetime) return;
        setSaving(true);
        try {
            const nextEvents = [
                ...events,
                {
                    id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`,
                    title: draft.title,
                    datetime: new Date(draft.datetime).toISOString(),
                    description: draft.description,
                    url: draft.url,
                },
            ];
            const res = await axios.patch(`${API}/sections/${section.id}`, { events: nextEvents });
            onUpdated && onUpdated(res.data);
            setDraft({ title: "", datetime: "", description: "", url: "" });
        } catch (e) { console.error(e); } finally { setSaving(false); }
    };

    const handleDelete = async (ev) => {
        try {
            const nextEvents = events.filter((e) => e.id !== ev.id);
            const res = await axios.patch(`${API}/sections/${section.id}`, { events: nextEvents });
            onUpdated && onUpdated(res.data);
        } catch (e) { console.error(e); }
    };

    return (
        <Group title="Event Calendar" accent={accent}>
            <div className="space-y-2 mb-4">
                {events.length === 0 && (
                    <div className="text-[10px] tracking-[0.3em] uppercase opacity-50" style={{ color: "#fff" }}>No events scheduled.</div>
                )}
                {events.map((ev) => (
                    <div key={ev.id} data-testid={`admin-event-${section.id}-${ev.id}`} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${accent}33` }}>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] tracking-[0.25em] uppercase" style={{ color: accent }}>
                                {new Date(ev.datetime).toLocaleString()}
                            </div>
                            <div className="text-sm truncate" style={{ color: "#fff" }}>{ev.title}</div>
                            {ev.url && (
                                <a href={ev.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase mt-1" style={{ color: accent }}>
                                    Open <ExternalLink size={10} />
                                </a>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => handleDelete(ev)}
                            data-testid={`admin-event-delete-${section.id}-${ev.id}`}
                            className="w-8 h-8 rounded-full grid place-items-center"
                            style={{ background: "rgba(255,154,154,0.1)", color: "#ff9a9a", border: "1px solid rgba(255,154,154,0.45)" }}
                        >
                            <Trash2 size={12} />
                        </button>
                    </div>
                ))}
            </div>
            <Field label="Add New Event">
                <input
                    type="text"
                    value={draft.title}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                    placeholder="Title"
                    data-testid={`admin-event-title-${section.id}`}
                    className="w-full rounded-xl px-4 py-2 text-sm outline-none mb-2"
                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                />
                <input
                    type="datetime-local"
                    value={draft.datetime}
                    onChange={(e) => setDraft({ ...draft, datetime: e.target.value })}
                    data-testid={`admin-event-datetime-${section.id}`}
                    className="w-full rounded-xl px-4 py-2 text-sm outline-none mb-2"
                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50`, colorScheme: "dark" }}
                />
                <input
                    type="url"
                    value={draft.url}
                    onChange={(e) => setDraft({ ...draft, url: e.target.value })}
                    placeholder="https:// (optional)"
                    data-testid={`admin-event-url-${section.id}`}
                    className="w-full rounded-xl px-4 py-2 text-sm outline-none mb-2"
                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                />
                <textarea
                    rows={2}
                    value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    placeholder="Description"
                    data-testid={`admin-event-description-${section.id}`}
                    className="w-full rounded-xl px-4 py-2 text-sm outline-none mb-2"
                    style={{ background: "rgba(255,255,255,0.06)", color: "#fff", border: `1px solid ${accent}50` }}
                />
                <button
                    type="button"
                    onClick={handleAdd}
                    disabled={saving || !draft.title || !draft.datetime}
                    data-testid={`admin-event-add-${section.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase disabled:opacity-50"
                    style={{ background: accent, color: "#0a0a0a" }}
                >
                    <Plus size={12} /> {saving ? "Saving…" : "Add Event"}
                </button>
            </Field>
        </Group>
    );
}


function MediaUploader({ section, kind, onUpdated, accent }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const items = kind === "photo"
        ? (section.media?.photos || [])
        : (section.media?.[kind + "s"] || []);

    const accept = kind === "video"
        ? "video/mp4,video/webm,video/quicktime,video/x-m4v,video/ogg"
        : kind === "audio"
            ? "audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/x-m4a,audio/aac,audio/flac"
            : "image/png,image/jpeg,image/jpg,image/webp";

    const icon = kind === "video" ? <Film size={14} /> : kind === "audio" ? <Music size={14} /> : <Camera size={14} />;
    const title = kind === "video" ? "Videos" : kind === "audio" ? "Audio Tracks" : "Photos (max 9)";

    const handleFile = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        setUploading(true);
        try {
            let updated = section;
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                fd.append("kind", kind);
                if (kind !== "photo") fd.append("title", f.name.replace(/\.[^.]+$/, ""));
                const res = await axios.post(`${API}/sections/${section.id}/media`, fd);
                updated = res.data;
            }
            onUpdated && onUpdated(updated);
        } catch (err) { console.error(err); } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const handleDelete = async (idx) => {
        try {
            const res = await axios.delete(`${API}/sections/${section.id}/media`, {
                params: { kind, index: idx },
            });
            onUpdated && onUpdated(res.data);
        } catch (e) { console.error(e); }
    };

    return (
        <Group title={title} accent={accent} icon={icon}>
            <div className="space-y-2 mb-3">
                {items.length === 0 && (
                    <div className="text-[10px] tracking-[0.3em] uppercase opacity-50" style={{ color: "#fff" }}>None yet.</div>
                )}
                {items.map((item, i) => {
                    const isPhoto = kind === "photo";
                    const url = isPhoto ? item : item.url;
                    const label = isPhoto ? `Photo ${i + 1}` : (item.title || `${kind} ${i + 1}`);
                    return (
                        <div key={`${url}-${i}`} data-testid={`admin-${kind}-item-${section.id}-${i}`} className="flex items-center gap-3 p-2 rounded-xl" style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${accent}33` }}>
                            {isPhoto && (
                                <img src={url} alt={label} className="w-12 h-12 object-cover rounded-md" />
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="text-sm truncate" style={{ color: "#fff" }}>{label}</div>
                                <code className="text-[9px] opacity-50 truncate block" style={{ color: "#fff" }}>{(url || "").split("/").pop()}</code>
                            </div>
                            <button
                                type="button"
                                onClick={() => handleDelete(i)}
                                data-testid={`admin-${kind}-delete-${section.id}-${i}`}
                                className="w-8 h-8 rounded-full grid place-items-center shrink-0"
                                style={{ background: "rgba(255,154,154,0.1)", color: "#ff9a9a", border: "1px solid rgba(255,154,154,0.45)" }}
                            >
                                <Trash2 size={12} />
                            </button>
                        </div>
                    );
                })}
            </div>
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                multiple
                className="hidden"
                data-testid={`admin-${kind}-input-${section.id}`}
                onChange={handleFile}
            />
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading || (kind === "photo" && items.length >= 9)}
                data-testid={`admin-${kind}-upload-${section.id}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase disabled:opacity-40"
                style={{ background: accent, color: "#0a0a0a" }}
            >
                <Plus size={12} />
                {uploading ? "Uploading…" : (kind === "photo" && items.length >= 9 ? "Gallery Full" : `Add ${kind}${kind === "audio" ? "" : "s"}`)}
            </button>
        </Group>
    );
}


/* -------- Bulk 4K folder uploader -------- */
function BulkUploader({ accent, onSectionsRefresh }) {
    const inputRef = useRef(null);
    const [files, setFiles] = useState([]);      // File[]
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [report, setReport] = useState(null);  // {applied, skipped, sections_updated}

    const addFiles = (fileList) => {
        const arr = Array.from(fileList || []).filter((f) => /image\//.test(f.type) || /\.(png|jpe?g|webp|avif)$/i.test(f.name));
        setFiles((prev) => [...prev, ...arr]);
        setReport(null);
    };

    const removeAt = (i) => {
        setFiles((prev) => prev.filter((_, idx) => idx !== i));
    };

    const clearAll = () => { setFiles([]); setReport(null); setProgress(0); };

    const parsePreview = (name) => {
        const n = (name || "").toLowerCase();
        const t = n.replace(/[-_]/g, " ");
        let sec = null;
        if (t.match(/(^|\s)(p0|center)(\s|\.|$)/)) sec = "Center Hub";
        else for (let i = 1; i <= 8; i++) if (t.match(new RegExp(`(^|\\s)p${i}(\\s|\\.|$)`))) { sec = `P${i}`; break; }
        if (!sec) return { section: "?", detail: "unrecognized" };
        const mode = t.includes("night") ? "Night" : (t.includes("day") ? "Day" : null);
        if (!mode) return { section: sec, detail: "missing day/night" };
        const kind = (t.includes("iso") || t.includes("aerial")) ? "ISO" : "360°";
        let view = "";
        if (kind === "360°") {
            if (t.includes("rear")) view = "Rear · ";
            else if (t.includes("interior")) view = "Interior · ";
            else if (t.includes("hallway")) view = "Hallway · ";
            else view = "Front · ";
        }
        return { section: sec, detail: `${kind} · ${view}${mode}` };
    };

    const submit = async () => {
        if (files.length === 0) return;
        setUploading(true); setProgress(0); setReport(null);
        try {
            const fd = new FormData();
            files.forEach((f) => fd.append("files", f, f.name));
            const res = await axios.post(`${API}/sections/bulk-4k-upload`, fd, {
                onUploadProgress: (p) => setProgress(Math.round((p.loaded * 100) / (p.total || 1))),
            });
            setReport(res.data);
            await onSectionsRefresh?.();
        } catch (e) {
            console.error(e);
            setReport({ applied: [], skipped: [{ file: "network", reason: String(e.message || e) }], sections_updated: [] });
        } finally {
            setUploading(false);
        }
    };

    return (
        <div data-testid="admin-bulk-4k" className="max-w-3xl">
            <SectionTitle accent={accent} eyebrow="Bulk · 4K Upload" title="Drop Your 4K Folder" />

            <div
                data-testid="bulk-4k-dropzone"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                    e.preventDefault(); setDragOver(false);
                    // Support both individual files and folder drops
                    const items = e.dataTransfer.items;
                    if (items && items.length && items[0].webkitGetAsEntry) {
                        const results = [];
                        const walk = (entry, path = "") => new Promise((resolve) => {
                            if (entry.isFile) {
                                entry.file((f) => { results.push(f); resolve(); });
                            } else if (entry.isDirectory) {
                                const reader = entry.createReader();
                                reader.readEntries(async (entries) => {
                                    for (const en of entries) await walk(en, path + entry.name + "/");
                                    resolve();
                                });
                            } else resolve();
                        });
                        Promise.all(Array.from(items).map((it) => walk(it.webkitGetAsEntry()))).then(() => addFiles(results));
                    } else {
                        addFiles(e.dataTransfer.files);
                    }
                }}
                onClick={() => inputRef.current?.click()}
                className="rounded-2xl px-6 py-10 text-center cursor-pointer transition-colors"
                style={{
                    background: dragOver ? `${accent}18` : "rgba(255,255,255,0.03)",
                    border: `2px dashed ${dragOver ? accent : accent + "55"}`,
                }}
            >
                <FolderUp size={40} className="mx-auto mb-3" style={{ color: accent }} />
                <div className="font-serif-display text-2xl tracking-luxe uppercase" style={{ color: "#fff" }}>
                    Drop a folder or images
                </div>
                <p className="text-xs mt-2 opacity-70" style={{ color: "#fff" }}>
                    Files are auto‑routed by name (P0/P1…P8, front / rear / hallway / interior / iso, day / night) and upscaled to 4K.
                </p>
                <div className="mt-4 inline-flex gap-2">
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                        data-testid="bulk-4k-pick-folder"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase"
                        style={{ background: accent, color: "#0a0a0a" }}
                    >
                        <FolderUp size={12} /> Pick Folder
                    </button>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); document.getElementById("bulk-4k-files-input")?.click(); }}
                        data-testid="bulk-4k-pick-files"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase"
                        style={{ background: "transparent", color: accent, border: `1px solid ${accent}80` }}
                    >
                        <UploadIcon size={12} /> Pick Files
                    </button>
                </div>
                <input
                    ref={inputRef}
                    type="file"
                    webkitdirectory=""
                    multiple
                    accept="image/*"
                    className="hidden"
                    data-testid="bulk-4k-folder-input"
                    onChange={(e) => addFiles(e.target.files)}
                />
                <input
                    id="bulk-4k-files-input"
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    data-testid="bulk-4k-files-input"
                    onChange={(e) => addFiles(e.target.files)}
                />
            </div>

            {files.length > 0 && (
                <div className="mt-6" data-testid="bulk-4k-queue">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-[10px] tracking-[0.35em] uppercase" style={{ color: accent }}>
                            {files.length} file{files.length === 1 ? "" : "s"} queued
                        </div>
                        <button
                            type="button"
                            onClick={clearAll}
                            data-testid="bulk-4k-clear"
                            className="text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full"
                            style={{ color: "#ff9a9a", border: "1px solid rgba(255,154,154,0.4)" }}
                        >
                            <Trash2 size={11} className="inline mr-1" /> Clear
                        </button>
                    </div>
                    <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                        {files.map((f, i) => {
                            const p = parsePreview(f.name);
                            const ok = p.section !== "?" && p.detail !== "unrecognized" && p.detail !== "missing day/night";
                            return (
                                <div
                                    key={`${f.name}-${i}`}
                                    data-testid={`bulk-4k-item-${i}`}
                                    className="flex items-center gap-3 p-2 rounded-xl"
                                    style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${ok ? accent + "33" : "rgba(255,154,154,0.35)"}` }}
                                >
                                    <span className="w-8 h-8 rounded-full grid place-items-center shrink-0"
                                        style={{ background: ok ? `${accent}22` : "rgba(255,154,154,0.1)", color: ok ? accent : "#ff9a9a" }}>
                                        {ok ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm truncate" style={{ color: "#fff" }}>{f.name}</div>
                                        <div className="text-[10px] tracking-[0.2em] uppercase opacity-70" style={{ color: "#fff" }}>
                                            {ok ? `${p.section} · ${p.detail}` : `Skipped: ${p.detail}`}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeAt(i)}
                                        data-testid={`bulk-4k-remove-${i}`}
                                        className="w-7 h-7 rounded-full grid place-items-center"
                                        style={{ background: "rgba(255,154,154,0.1)", color: "#ff9a9a" }}
                                    >
                                        <X size={11} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    <button
                        type="button"
                        onClick={submit}
                        disabled={uploading}
                        data-testid="bulk-4k-submit"
                        className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[10px] tracking-[0.35em] uppercase disabled:opacity-60"
                        style={{ background: accent, color: "#0a0a0a" }}
                    >
                        <UploadIcon size={12} />
                        {uploading ? `Uploading… ${progress}%` : `Upload & 4K Upscale (${files.length})`}
                    </button>
                </div>
            )}

            {report && (
                <div className="mt-6" data-testid="bulk-4k-report">
                    <div className="text-[10px] tracking-[0.35em] uppercase mb-2" style={{ color: accent }}>Report</div>
                    <div className="text-sm mb-2" style={{ color: "#fff" }}>
                        Applied: <b>{report.applied?.length || 0}</b> · Skipped: <b>{report.skipped?.length || 0}</b> · Sections updated: <b>{report.sections_updated?.length || 0}</b>
                    </div>
                    {(report.skipped || []).length > 0 && (
                        <div className="rounded-xl p-3 space-y-1" style={{ background: "rgba(255,154,154,0.06)", border: "1px solid rgba(255,154,154,0.35)" }}>
                            {report.skipped.map((s, i) => (
                                <div key={i} className="text-[11px]" style={{ color: "#ffbcbc" }}>
                                    <b>{s.file}</b> — {s.reason}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


/* -------- Helpers -------- */
function SectionTitle({ accent, eyebrow, title }) {
    return (
        <div className="mb-6 pb-4" style={{ borderBottom: `1px solid ${accent}33` }}>
            <div className="text-[10px] tracking-[0.4em] uppercase" style={{ color: accent }}>{eyebrow}</div>
            <h2 className="mt-1 font-serif-display text-3xl md:text-4xl uppercase tracking-luxe" style={{ color: "#fff" }}>
                {title}
            </h2>
        </div>
    );
}

function Group({ title, accent, icon, children }) {
    return (
        <section
            className="rounded-2xl p-5"
            style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${accent}33` }}
        >
            <div className="flex items-center gap-2 mb-4 text-[10px] tracking-[0.35em] uppercase" style={{ color: accent }}>
                {icon}
                {title}
            </div>
            <div className="space-y-3">{children}</div>
        </section>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="text-[10px] tracking-[0.25em] uppercase mb-1 opacity-65" style={{ color: "#fff" }}>{label}</div>
            {children}
        </div>
    );
}
